<template>
    <div class="canvas-bg" aria-hidden="true">
        <div class="dots"></div>
        <div class="trail" ref="trail"></div>
        <div class="dots-hot"></div>
    </div>
</template>

<script>
function attachTrail(page, host) {
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var hoverNone = window.matchMedia && window.matchMedia('(hover: none)').matches;
    if (reduce || hoverNone || !page || !host) return function () {};

    var N = 22;
    var HEAD = 92;
    var HIST = 110;
    var S = 0.17;
    var K = 0.16;
    var DAMP = 0.74;

    var cv = document.createElement('canvas');
    var ctx = cv.getContext('2d');
    host.appendChild(cv);

    function sprite(r, g, b) {
        var size = 64;
        var c = document.createElement('canvas');
        c.width = c.height = size;
        var x = c.getContext('2d');
        var gr = x.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
        gr.addColorStop(0.00, 'rgba(' + r + ',' + g + ',' + b + ',0.85)');
        gr.addColorStop(0.28, 'rgba(' + r + ',' + g + ',' + b + ',0.46)');
        gr.addColorStop(0.58, 'rgba(' + r + ',' + g + ',' + b + ',0.16)');
        gr.addColorStop(0.80, 'rgba(' + r + ',' + g + ',' + b + ',0.04)');
        gr.addColorStop(1.00, 'rgba(' + r + ',' + g + ',' + b + ',0)');
        x.fillStyle = gr;
        x.fillRect(0, 0, size, size);
        return c;
    }

    var warm = sprite(255, 156, 74);
    var cool = sprite(74, 132, 246);
    var core = sprite(255, 214, 170);

    var vw = 0;
    var vh = 0;
    function resize() {
        vw = window.innerWidth;
        vh = window.innerHeight;
        cv.width = Math.max(1, Math.round(vw * S));
        cv.height = Math.max(1, Math.round(vh * S));
    }
    resize();
    var rzT;
    function onResize() {
        clearTimeout(rzT);
        rzT = setTimeout(resize, 150);
    }
    window.addEventListener('resize', onResize);

    var px = -999;
    var py = -999;
    var dx = px;
    var dy = py;
    var hx = px;
    var hy = py;
    var vhx = 0;
    var vhy = 0;
    var sx2 = px;
    var sy2 = py;
    var hist = [];

    var speed = 0;
    var lastMove = 0;
    var raf = null;
    var wasVisible = false;
    var life = 0;
    var HOLD = 260;
    var FADE = 1300;
    var RISE = 0.16;
    var SINK = 0.030;
    var MAXA = 0.72;
    var PITCH = parseFloat(getComputedStyle(page).getPropertyValue('--dot-size')) || 22;
    var lastQX = null;
    var lastQY = null;
    var suspended = false;

    function loop() {
        var idleFor = performance.now() - lastMove;

        dx += (px - dx) * 0.14;
        dy += (py - dy) * 0.14;
        var qx = Math.round(dx / PITCH) * PITCH;
        var qy = Math.round(dy / PITCH) * PITCH;
        if (qx !== lastQX || qy !== lastQY) {
            lastQX = qx;
            lastQY = qy;
            page.style.setProperty('--hx', qx + 'px');
            page.style.setProperty('--hy', qy + 'px');
        }

        vhx = (vhx + (px - hx) * K) * DAMP;
        vhy = (vhy + (py - hy) * K) * DAMP;
        hx += vhx;
        hy += vhy;

        var inst = Math.sqrt(vhx * vhx + vhy * vhy);
        speed += (inst - speed) * 0.10;

        sx2 += (hx - sx2) * 0.34;
        sy2 += (hy - sy2) * 0.34;

        hist.unshift(sx2);
        hist.unshift(sy2);
        if (hist.length > HIST * 2) {
            hist.pop();
            hist.pop();
        }

        var wantSpeed = Math.min(1, speed / 1.5);
        var wantIdle = 1 - Math.min(1, Math.max(0, (idleFor - HOLD) / FADE));
        var want = Math.min(wantSpeed, wantIdle);
        want = want * want * (3 - 2 * want);

        life += (want - life) * (want > life ? RISE : SINK);

        host.style.opacity = (life * MAXA).toFixed(3);

        if (life > 0.02) {
            draw();
            wasVisible = true;
        } else if (wasVisible) {
            ctx.clearRect(0, 0, cv.width, cv.height);
            host.style.opacity = '0';
            wasVisible = false;
        }

        var settled = Math.abs(px - dx) < 0.3 && Math.abs(py - dy) < 0.3 && speed < 0.35;
        if (!settled || life > 0.02) raf = requestAnimationFrame(loop);
        else raf = null;
    }

    function pt(i) {
        return { x: hist[i * 2 + 1], y: hist[i * 2] };
    }

    function draw() {
        ctx.clearRect(0, 0, cv.width, cv.height);

        var count = hist.length / 2;
        var ease = 0.30 + 0.70 * life;
        var tailLen = Math.min(820, 90 + speed * 26) * ease;
        var step = tailLen / (N - 1);
        var width = Math.max(22, Math.min(HEAD, step * 5.4)) * (0.55 + 0.45 * life);

        var hi = 0;
        var walked = 0;

        for (var i = 0; i < N; i++) {
            var need = i * step;

            while (hi < count - 2) {
                var a0 = pt(hi);
                var b0 = pt(hi + 1);
                var sl = Math.sqrt((b0.x - a0.x) * (b0.x - a0.x) + (b0.y - a0.y) * (b0.y - a0.y));
                if (walked + sl >= need || sl === 0) break;
                walked += sl;
                hi++;
            }

            var p0 = pt(hi);
            var p1 = pt(Math.min(hi + 1, count - 1));
            var vx2 = p1.x - p0.x;
            var vy2 = p1.y - p0.y;
            var sl2 = Math.sqrt(vx2 * vx2 + vy2 * vy2) || 1;
            var f = Math.min(1, Math.max(0, (need - walked) / sl2));

            var x = p0.x + vx2 * f;
            var y = p0.y + vy2 * f;
            var u = i / (N - 1);

            var lead = Math.min(1, u / 0.14);
            var body = Math.pow(1 - u, 0.62);
            var d = Math.max(6, width * (0.34 + 0.66 * lead) * body) * 2.3;
            var r = d * S * 0.5;

            var mix = u * u * (3 - 2 * u);
            var alpha = Math.pow(1 - u, 1.25 + (1 - life) * 1.6) * 0.9;

            var nx = -vy2 / sl2;
            var ny = vx2 / sl2;
            var sep = (1.2 + u * 3.2);
            var cd = r * 2;

            if (i % 4 === 0) {
                var hr = r * 2.4;
                ctx.globalAlpha = alpha * 0.10;
                ctx.drawImage(mix < 0.5 ? warm : cool, x * S - hr, y * S - hr, hr * 2, hr * 2);
            }

            if (mix < 0.92) {
                ctx.globalAlpha = alpha * (1 - mix);
                ctx.drawImage(warm, (x - nx * sep) * S - r, (y - ny * sep) * S - r, cd, cd);
            }
            if (mix > 0.08) {
                ctx.globalAlpha = alpha * mix;
                ctx.drawImage(cool, (x + nx * sep) * S - r, (y + ny * sep) * S - r, cd, cd);
            }

            if (u < 0.10) {
                ctx.globalAlpha = (1 - u / 0.10) * 0.28;
                var kr = r * 0.42;
                ctx.drawImage(core, x * S - kr, y * S - kr, kr * 2, kr * 2);
            }
        }
        ctx.globalAlpha = 1;
    }

    function onVisibility() {
        suspended = document.hidden;
        if (!suspended && !raf) raf = requestAnimationFrame(loop);
    }

    function onPointerMove(e) {
        if (e.pointerType === 'touch' || suspended) return;
        if (px === -999) {
            px = e.clientX;
            py = e.clientY;
            dx = px;
            dy = py;
            hx = px;
            hy = py;
        }
        px = e.clientX;
        py = e.clientY;
        lastMove = performance.now();
        page.classList.add('pointer-live');
        if (!raf) raf = requestAnimationFrame(loop);
    }

    function onMouseLeave() {
        page.classList.remove('pointer-live');
        lastMove = 0;
        if (!raf) raf = requestAnimationFrame(loop);
    }

    function onMouseEnter() {
        page.classList.add('pointer-live');
    }

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pointermove', onPointerMove, { passive: true });
    document.addEventListener('mouseleave', onMouseLeave);
    document.addEventListener('mouseenter', onMouseEnter);

    return function teardown() {
        if (raf) cancelAnimationFrame(raf);
        clearTimeout(rzT);
        window.removeEventListener('resize', onResize);
        document.removeEventListener('visibilitychange', onVisibility);
        window.removeEventListener('pointermove', onPointerMove);
        document.removeEventListener('mouseleave', onMouseLeave);
        document.removeEventListener('mouseenter', onMouseEnter);
        page.classList.remove('pointer-live');
        page.style.removeProperty('--hx');
        page.style.removeProperty('--hy');
        host.style.opacity = '';
        if (cv.parentNode === host) host.removeChild(cv);
    };
}

export default {
    name: 'PricingBackdrop',
    mounted() {
        this._teardownTrail = attachTrail(this.$el.closest('.pricing-page'), this.$refs.trail);
    },
    beforeDestroy() {
        if (this._teardownTrail) this._teardownTrail();
    }
};
</script>
